import React, { DependencyList, ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { CommonModal, CommonModalComponentProps } from "./CommonModal";
import Modal from "./Modal";

export const useDeleteModal = ({
  headerText,
  subheaderText,
  Content,
  deleteButtonText,
  size,
  onDelete,
  dependencies,
  disabled,
}: {
  headerText: string;
  subheaderText?: string;
  Content: ReactNode;
  deleteButtonText: string;
  size?: CommonModalComponentProps["size"];
  onDelete: () => void;
  dependencies?: DependencyList;
  disabled?: boolean;
}) => {
  const [showDeleteModal, hideDeleteModal] = useModal(
    () => (
      <Modal onRequestClose={hideDeleteModal}>
        <DeleteModalForm
          headerText={headerText}
          subheaderText={subheaderText}
          Content={Content}
          deleteButtonText={deleteButtonText}
          size={size}
          onClose={hideDeleteModal}
          onDelete={onDelete}
          disabled={disabled}
        />
      </Modal>
    ),
    dependencies ? [...dependencies] : undefined,
  );

  return [showDeleteModal, hideDeleteModal] as const;
};

export type DeleteModalFormProps = {
  headerText: string;
  subheaderText?: string;
  Content: ReactNode;
  deleteButtonText?: string;
  onClose: () => void;
  onDelete: () => void;
  disabled?: boolean;
} & Pick<CommonModalComponentProps, "size">;

export const DeleteModalForm = ({
  headerText,
  subheaderText,
  Content,
  deleteButtonText = "Yes, delete",
  size,
  onClose,
  onDelete,
  disabled = false,
}: DeleteModalFormProps) => {
  const { handleSubmit } = useForm();

  const onSubmit = handleSubmit(() => {
    onDelete();
    onClose();
  });

  return (
    <CommonModal
      title={headerText}
      subtitle={subheaderText}
      onSubmit={onSubmit}
      onCancel={onClose}
      loading={false}
      disabled={disabled}
      size={size}
      variant="destructive"
      primaryButtonText={deleteButtonText}
      content={Content}
    />
  );
};
